import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Instagram, ExternalLink, Loader2, Car } from 'lucide-react';
import { restaurantInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useLanguage();

  React.useEffect(() => {
    document.title = 'Contact - Ya Habibi! Restaurant Libanais Genève | 27 rue de Berne';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error('Erreur serveur');
      toast.success(t('contact', 'successMsg'));
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch {
      toast.error(t('contact', 'errorMsg'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen pt-24" data-testid="contact-page">
      <section className="bg-[#F5E6D3] py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#0f172a] mb-4">
            {t('contact', 'title')}
          </h1>
          <p className="text-lg text-[#0f172a]/80 max-w-2xl mx-auto">
            {t('contact', 'subtitle')}
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-serif text-2xl font-bold text-[#0f172a] mb-8">{t('contact', 'coordinates')}</h2>
              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4 p-4 bg-[#f8fafc] rounded-xl">
                  <div className="w-12 h-12 bg-[#F5E6D3] rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin size={22} className="text-[#8B4513]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0f172a] mb-1">{t('contact', 'address')}</h3>
                    <p className="text-[#64748b]">{restaurantInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-[#f8fafc] rounded-xl">
                  <div className="w-12 h-12 bg-[#F5E6D3] rounded-full flex items-center justify-center flex-shrink-0">
                    <Car size={22} className="text-[#8B4513]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0f172a] mb-1">{t('contact', 'parkings')}</h3>
                    <div className="text-[#64748b] space-y-2 mt-1">
                      {restaurantInfo.parkings.map((p, i) => (
                        <div key={i}>
                          <p className="font-medium text-[#0f172a] text-sm">{p.name}</p>
                          <p className="text-sm">{p.address}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-[#f8fafc] rounded-xl">
                  <div className="w-12 h-12 bg-[#F5E6D3] rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone size={22} className="text-[#8B4513]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0f172a] mb-1">{t('contact', 'phone')}</h3>
                    <p className="text-[#64748b]">{restaurantInfo.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-[#f8fafc] rounded-xl">
                  <div className="w-12 h-12 bg-[#F5E6D3] rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail size={22} className="text-[#8B4513]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0f172a] mb-1">{t('contact', 'email')}</h3>
                    <p className="text-[#64748b]">{restaurantInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-[#f8fafc] rounded-xl">
                  <div className="w-12 h-12 bg-[#F5E6D3] rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock size={22} className="text-[#8B4513]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0f172a] mb-1">{t('contact', 'hours')}</h3>
                    <div className="text-[#64748b] space-y-1">
                      <p className="text-green-600 font-medium">{t('contact', 'open7')}</p>
                      <p>{restaurantInfo.hours.weekdaysLabel}: {restaurantInfo.hours.weekdays}</p>
                      <p>{restaurantInfo.hours.weekendLabel}: {restaurantInfo.hours.weekend}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-[#0f172a] mb-4">{t('contact', 'followUs')}</h3>
                <div className="flex gap-4">
                  <a href={restaurantInfo.social.instagram} target="_blank" rel="noopener noreferrer" className="w-14 h-14 bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] rounded-full flex items-center justify-center text-white hover:opacity-90 transition-opacity" aria-label="Instagram">
                    <Instagram size={26} />
                  </a>
                  <a href={restaurantInfo.social.googleBusiness} target="_blank" rel="noopener noreferrer" className="w-14 h-14 bg-[#4285F4] rounded-full flex items-center justify-center text-white hover:opacity-90 transition-opacity" aria-label="Google Business">
                    <ExternalLink size={26} />
                  </a>
                </div>
              </div>
            </div>

            <div>
              <h2 className="font-serif text-2xl font-bold text-[#0f172a] mb-8">{t('contact', 'sendMessage')}</h2>
              <form onSubmit={handleSubmit} className="bg-[#f8fafc] rounded-2xl p-8" data-testid="contact-form">
                <div className="space-y-6">
                  <div>
                    <label className="block text-[#0f172a] font-medium mb-2">{t('contact', 'fullName')}</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5E6D3] focus:border-transparent outline-none transition-all bg-white" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className="block text-[#0f172a] font-medium mb-2">{t('contact', 'emailLabel')}</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5E6D3] focus:border-transparent outline-none transition-all bg-white" data-testid="contact-email-input" />
                  </div>
                  <div>
                    <label className="block text-[#0f172a] font-medium mb-2">{t('contact', 'subject')}</label>
                    <select name="subject" value={formData.subject} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5E6D3] focus:border-transparent outline-none transition-all bg-white" data-testid="contact-subject-select">
                      <option value="">{t('contact', 'selectSubject')}</option>
                      <option value="reservation">{t('contact', 'subjectReservation')}</option>
                      <option value="menu">{t('contact', 'subjectMenu')}</option>
                      <option value="event">{t('contact', 'subjectEvent')}</option>
                      <option value="feedback">{t('contact', 'subjectFeedback')}</option>
                      <option value="other">{t('contact', 'subjectOther')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#0f172a] font-medium mb-2">{t('contact', 'message')}</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} required rows="5" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5E6D3] focus:border-transparent outline-none transition-all resize-none bg-white" placeholder={t('contact', 'messagePlaceholder')} data-testid="contact-message-input" />
                  </div>
                  <button type="submit" disabled={isLoading} className="w-full bg-[#0f172a] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#1e293b] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="contact-submit-button">
                    {isLoading ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                    {isLoading ? t('contact', 'sending') : t('contact', 'submit')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="h-96 bg-gray-200">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2761.024676776044!2d6.142083776671875!3d46.20858447108516!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478c652b1c8c1cc5%3A0x6e8a6c9dd5c6d7b6!2sRue%20de%20Berne%2027%2C%201201%20Gen%C3%A8ve%2C%20Suisse!5e0!3m2!1sfr!2sfr!4v1704067200000!5m2!1sfr!2sfr"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Localisation Ya Habibi"
        />
      </section>

      <section className="py-16 bg-[#F5E6D3]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl font-bold text-[#0f172a] mb-4">{t('contact', 'readyToVisit')}</h2>
          <p className="text-lg text-[#0f172a]/80 mb-8">{t('contact', 'waitingForYou')}</p>
          <a href="https://www.google.com/maps/dir//Rue+de+Berne+27,+1201+Gen%C3%A8ve,+Suisse" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#0f172a] text-white px-8 py-4 rounded-md font-semibold hover:bg-[#1e293b] transition-all duration-200 hover:-translate-y-1 shadow-lg">
            <MapPin size={20} />
            {t('contact', 'directions')}
          </a>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
